import { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { getAiUsage, getAdminAiUsage } from "services/tenantUserAdminService";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

export default function AiUsage() {
  const { tenantId, isSuperAdmin, isTenantAdmin } = useAuthz();
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const load = async () => {
      try {
        let res;
        if (isSuperAdmin && selectedTenant) {
          const data = await getAdminAiUsage({ tenantId: selectedTenant, start: startDate ? startDate.toISOString() : undefined, end: endDate ? endDate.toISOString() : undefined, limit: 500 });
          res = data.usage || data;
        } else if (isSuperAdmin) {
          const data = await getAdminAiUsage({ start: startDate ? startDate.toISOString() : undefined, end: endDate ? endDate.toISOString() : undefined, limit: 500 });
          res = data.usage || data;
        } else if (tenantId) {
          const data = await getAiUsage(tenantId, { start: startDate ? startDate.toISOString() : undefined, end: endDate ? endDate.toISOString() : undefined, limit: 500 });
          res = data.usage || data.usage || data;
        } else {
          res = [];
        }
        const rows = Array.isArray(res) ? res : (res.events || []);
        setUsage(rows);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId, isSuperAdmin, isTenantAdmin, selectedTenant, startDate, endDate]);

  // Aggregate per-tenant when super-admin
  const tenantSummary = (isSuperAdmin && Array.isArray(usage)) ? usage.reduce((acc, u) => {
    const t = u.tenantId || 'unknown';
    acc[t] = acc[t] || { tenantId: t, events: 0, totalTokens: 0, totalCost: 0 };
    acc[t].events += 1;
    acc[t].totalTokens += (u.totalTokens || 0);
    acc[t].totalCost += (u.estimatedCost || 0) || 0;
    return acc;
  }, {}) : {};

  const tenantRows = Object.values(tenantSummary || {}).sort((a,b) => b.totalTokens - a.totalTokens);

  const totalTokens = usage.reduce((s, u) => s + (u.totalTokens || 0), 0);
  const totalCost = usage.reduce((s, u) => s + (u.estimatedCost || 0), 0);

  // Prepare chart data (tokens per day)
  const tokensByDay = usage.reduce((acc, u) => {
    const day = u.createdAt ? new Date(u.createdAt).toISOString().slice(0,10) : 'unknown';
    acc[day] = acc[day] || { date: day, tokens: 0 };
    acc[day].tokens += (u.totalTokens || 0);
    return acc;
  }, {});
  const chartData = Object.values(tokensByDay).sort((a,b) => a.date.localeCompare(b.date));

  const opBreakdown = Object.values(usage.reduce((acc, u) => {
    const op = u.operation || 'unknown';
    acc[op] = acc[op] || { operation: op, tokens: 0, events: 0 };
    acc[op].tokens += (u.totalTokens || 0);
    acc[op].events += 1;
    return acc;
  }, {}));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <VuiTypography variant="h4">AI Usage</VuiTypography>
        <VuiBox display="flex" gap={1} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker label="Start" value={startDate} onChange={(v) => setStartDate(v ? v.toDate() : null)} renderInput={(params) => <TextField {...params} size="small" />} />
            <DatePicker label="End" value={endDate} onChange={(v) => setEndDate(v ? v.toDate() : null)} renderInput={(params) => <TextField {...params} size="small" />} />
          </LocalizationProvider>
          {isSuperAdmin && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="tenant-select-label">Tenant</InputLabel>
              <Select labelId="tenant-select-label" value={selectedTenant || ''} label="Tenant" onChange={(e) => setSelectedTenant(e.target.value || null)}>
                <MenuItem value="">All Tenants</MenuItem>
                {tenantRows.map(t => <MenuItem key={t.tenantId} value={t.tenantId}>{t.tenantId}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <VuiButton color="info" size="small" onClick={() => { setSelectedTenant(null); setStartDate(null); setEndDate(null); window.location.reload(); }}>Reset</VuiButton>
        </VuiBox>
      </VuiBox>

      <Card sx={{ p: 2, mb: 2 }}>
        <VuiBox mb={2} display="flex" gap={2} alignItems="center">
          <VuiTypography variant="button" color="text">Total tokens:</VuiTypography>
          <VuiTypography variant="h6">{totalTokens}</VuiTypography>
          <VuiTypography variant="button" color="text">Estimated cost:</VuiTypography>
          <VuiTypography variant="h6">{isNaN(totalCost) ? '—' : `£${totalCost.toFixed(2)}`}</VuiTypography>
        </VuiBox>

        {isSuperAdmin && (
          <VuiBox mb={2}>
            <VuiTypography variant="button">Tenants</VuiTypography>
            {loading ? <LoadingSpinner /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenant</TableCell>
                      <TableCell>Events</TableCell>
                      <TableCell>Tokens</TableCell>
                      <TableCell>Est. Cost</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenantRows.map((t) => (
                      <TableRow key={t.tenantId}>
                        <TableCell>{t.tenantId}</TableCell>
                        <TableCell>{t.events}</TableCell>
                        <TableCell>{t.totalTokens}</TableCell>
                        <TableCell>{t.totalCost ? `£${Number(t.totalCost).toFixed(2)}` : '—'}</TableCell>
                        <TableCell>
                          <VuiButton size="small" color="info" onClick={async () => {
                            setLoading(true);
                            setSelectedTenant(t.tenantId);
                            try {
                              const data = await getAdminAiUsage({ tenantId: t.tenantId, limit: 500, start: startDate ? startDate.toISOString() : undefined, end: endDate ? endDate.toISOString() : undefined });
                              const rows = data.usage || data;
                              setUsage(Array.isArray(rows) ? rows : (rows.events || []));
                            } catch (e) {
                              setError(e.message || String(e));
                            } finally { setLoading(false); }
                          }}>View details</VuiButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </VuiBox>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <VuiTypography color="error">Error: {error}</VuiTypography>
        ) : (
          <>
            <VuiBox mb={2} sx={{ height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="tokens" stroke="#1976d2" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </VuiBox>

            <VuiBox mb={2} display="flex" gap={2}>
              <VuiBox flex={1} sx={{ height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={opBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="operation" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="tokens" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </VuiBox>

              <VuiBox sx={{ width: 200, height: 200 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={opBreakdown} dataKey="tokens" nameKey="operation" cx="50%" cy="50%" innerRadius={30} outerRadius={60} label>
                      {opBreakdown.map((entry, idx) => (
                        <Cell key={`c-${idx}`} fill={["#0088FE", "#00C49F", "#FFBB28", "#FF8042"][idx % 4]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </VuiBox>
            </VuiBox>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Operation</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Tokens</TableCell>
                    <TableCell>Est. Cost</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usage.map((u) => (
                    <TableRow key={u.id || `${u.createdAt}-${Math.random()}` }>
                      <TableCell>{new Date(u.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{u.tenantId}</TableCell>
                      <TableCell>{u.operation}</TableCell>
                      <TableCell>{u.model}</TableCell>
                      <TableCell>{u.totalTokens || 0}</TableCell>
                      <TableCell>{u.estimatedCost != null ? `£${Number(u.estimatedCost).toFixed(2)}` : '—'}</TableCell>
                      <TableCell>{u.userId || '—'}</TableCell>
                      <TableCell style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{JSON.stringify(u.detail || {})}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Card>

      <Footer />
    </DashboardLayout>
  );
}
